import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SplashScreen } from '@capacitor/splash-screen';
import { useAuth } from '@/contexts';
import { isNativePlatform } from '@/lib/platform';
import { error } from '@/utils/logger';

/**
 * Hard ceiling on how long the splash screen can remain after launch. The
 * primary path hides it as soon as auth resolves, but if auth never resolves
 * (backend down, network outage, JS error during boot), the splash would sit
 * indefinitely. This timeout guarantees the user always reaches some UI.
 */
const SPLASH_SAFETY_TIMEOUT_MS = 5000;

/**
 * Hides the Capacitor splash screen at the earliest moment we have something
 * meaningful to show:
 *  - auth has finished initializing, OR
 *  - we're on a share-link resolver (`/s/...`) which renders its own progress
 *    UI while auth keeps loading in the background.
 *
 * A safety timeout ensures the splash always dismisses within 5s regardless
 * of app state.
 *
 * No-op on web. Call once near the app root.
 */
export function useNativeSplashScreen(): void {
    const { isLoading } = useAuth();
    const { pathname } = useLocation();
    const hiddenRef = useRef(false);

    const hide = (reason: string) => {
        if (hiddenRef.current) return;
        hiddenRef.current = true;
        SplashScreen.hide().catch((e) => {
            error(`[useNativeSplashScreen] Failed to hide splash (${reason})`, e);
        });
    };

    useEffect(() => {
        if (!isNativePlatform()) return;
        const isShareShortLink = pathname.startsWith('/s/');
        if (isLoading && !isShareShortLink) return;
        hide('auth-ready');
    }, [isLoading, pathname]);

    useEffect(() => {
        if (!isNativePlatform()) return;
        const timeoutId = window.setTimeout(() => {
            hide('safety-timeout');
        }, SPLASH_SAFETY_TIMEOUT_MS);
        return () => window.clearTimeout(timeoutId);
    }, []);
}
